import {
    ANALYTICS_CHANNEL,
    CHANNEL_INFO_BY_CHANNEL,
    CHANNEL_TYPE_ANALYTICS,
    CHANNEL_TYPE_ECOMMERCE,
    CHANNEL_TYPE_EMAIL_MARKETING,
    CHANNEL_TYPE_PAID_MEDIA,
    CHANNEL_TYPE_PROFITABILITY,
    CHANNEL_TYPE_SEO,
    FACEBOOK_CHANNEL,
    SEARCH_CONSOLE_CHANNEL,
    SHEET_CHANNEL,
    TIENDA_NUBE_CHANNEL,
    ChannelsHelper,
} from '../../channels/channels';
import {ERROR_KIND_ACCOUNT_DISABLED, isFixedInTheSpreadsheet, resolveErrorKind} from '../../channels/adAccountErrorKinds';
import {platformName} from '../../../utils/filterNotes';

/**
 * Conexiones › Cuentas en la 2.1, sin red ni React (tanda «Agencia 2.1», contrato §3).
 *
 * Todo lo que la pantalla DECIDE vive acá y se testea acá: qué vista se abre, cómo se agrupa, qué
 * coincide con la búsqueda, cuántas filas se dibujan, a qué grupo de actividad va cada cuenta sin
 * cliente, qué cliente se sugiere y qué quedó de una acción en lote. Los componentes sólo pintan.
 *
 * La pantalla clásica dibujaba las ~580 cuentas de una agencia grande de una vez y congelaba el
 * navegador (diagnóstico del 04/10). Acá se dibujan de a `PAGE_SIZE` por vista.
 */

export const VIEW_LINKED = 'linked';
export const VIEW_UNLINKED = 'unlinked';
export const VIEW_IGNORED = 'ignored';
export const VIEWS = [VIEW_LINKED, VIEW_UNLINKED, VIEW_IGNORED];

export const PAGE_SIZE = 50;

export const KIND_ACCOUNT = 'account';
export const KIND_STORE = 'store';
export const KIND_PROFITABILITY = 'profitability';

// ------------------------------------------------------------------ parámetros de la URL

// `?vista=` usa las palabras de la pantalla, no los nombres internos: es un link que se comparte.
const VIEW_BY_PARAM = {'sin-cliente': VIEW_UNLINKED, 'ignoradas': VIEW_IGNORED};
const PARAM_BY_VIEW = {[VIEW_UNLINKED]: 'sin-cliente', [VIEW_IGNORED]: 'ignoradas'};

/** La vista que pide `?vista=`; sin el parámetro (o con uno que no existe), «Conectadas». */
export function viewFromParam(value) {
    return VIEW_BY_PARAM[String(value || '').trim().toLowerCase()] || VIEW_LINKED;
}

/** El valor de `?vista=` de una vista; `null` para la de entrada, que no lo necesita. */
export function paramOfView(view) {
    return PARAM_BY_VIEW[view] || null;
}

// `?acceso=sin` (tanda «Reconexión de pauta», P-4): sólo las cuentas sin acceso que están en alguna línea
// de seguimiento activa. Lo usan «Ver cuáles» del aviso de arriba y el «Reconectar» de varias cuentas
// de la fila del Tablero.
export const ACCESS_PARAM_NONE = 'sin';

/** ¿`?acceso=` pide sólo las cuentas sin acceso? */
export function unreachableOnlyFromParam(value) {
    return String(value || '').trim().toLowerCase() === ACCESS_PARAM_NONE;
}

// Los nombres con que alguien puede escribir el canal a mano en un link. El contrato es el código
// del backend (`?plataforma=FACEBOOK`), y estos alias no cuestan nada.
const CHANNEL_ALIASES = {
    META: FACEBOOK_CHANNEL,
    GA4: ANALYTICS_CHANNEL,
    SEARCH_CONSOLE: SEARCH_CONSOLE_CHANNEL,
    TIENDANUBE: TIENDA_NUBE_CHANNEL,
};

/** El canal que pide `?plataforma=`, o `null` (= «Todas») si no es uno que el bundle conozca. */
export function platformFromParam(value) {
    const code = String(value || '').trim().toUpperCase();
    if (!code) return null;
    const channel = CHANNEL_ALIASES[code] || code;
    return CHANNEL_INFO_BY_CHANNEL[channel] ? channel : null;
}

/**
 * `?ignore=CANAL||idDeLaCuenta`, el link de los avisos y del mail de cuentas sin monitorear
 * (`send_ad_accounts_not_monitored_report.py`). El id es el de la PLATAFORMA, no el de Dashbo.
 */
export function parseIgnoreParam(value) {
    const raw = String(value || '');
    if (!raw.includes('||')) return null;
    const [channel, accountId] = raw.split('||');
    if (!channel || !channel.trim() || !accountId || !accountId.trim()) return null;
    return {channel: channel.trim(), accountId: accountId.trim()};
}

function sameAccountId(a, b) {
    const clean = (value) => String(value === null || value === undefined ? '' : value)
        .toLowerCase().replace(/[^a-z0-9]/g, '');
    const left = clean(a);
    return left !== '' && left === clean(b);
}

/**
 * La cuenta sin cliente que nombra un `?ignore=`.
 *
 * Se compara como TEXTO. La pantalla clásica hace `parseInt` del id y lo compara con `===` contra
 * `account_id`, que el backend manda como string (`AdAccount.data()`): un número nunca es igual a
 * un string, así que allá el link no abre nada. Acá sí.
 */
export function findIgnoreTarget(unlinkedRows, target) {
    if (!target) return null;
    return (unlinkedRows || []).find((row) => row.channel === target.channel
        && sameAccountId(row.accountId, target.accountId)) || null;
}

// ------------------------------------------------------------------ las filas

function asText(value) {
    return value === null || value === undefined ? '' : String(value);
}

/** Una conexión de la familia «Rentabilidad del negocio» (Escalafy): no es una cuenta publicitaria. */
export function isProfitability(raw) {
    return !!raw && (ChannelsHelper.channelIsProfitability(raw.channel)
        || raw.channel_type === CHANNEL_TYPE_PROFITABILITY);
}

function linkedRowOf(raw, kind) {
    return {
        key: `l-${raw.id}`,
        kind,
        channel: raw.channel,
        name: asText(raw.name),
        accountId: asText(raw.account_id),
        clientId: raw.agency_client_id,
        clientName: asText(raw.agency_client_name),
        raw,
    };
}

function unlinkedRowOf(raw) {
    return {
        key: `u-${raw.id}`,
        kind: KIND_ACCOUNT,
        adAccountPk: raw.id,
        channel: raw.channel,
        name: asText(raw.name),
        accountId: asText(raw.account_id),
        clientName: '',
        raw,
    };
}

function ignoredRowOf(entry) {
    const raw = entry.ad_account;
    const snooze = entry.alert_snooze || {};
    return {
        key: `i-${raw.id}`,
        kind: KIND_ACCOUNT,
        adAccountPk: raw.id,
        channel: raw.channel,
        name: asText(raw.name),
        accountId: asText(raw.account_id),
        clientName: '',
        snoozedBy: snooze.snooze_by_email || null,
        snoozedOn: snooze.snooze_on || null,
        raw,
    };
}

/**
 * `GET agency/ad-accounts/` en las tres listas de la pantalla. «Conectadas» junta cuentas, tiendas y
 * Escalafy (el backend manda las tiendas aparte y Escalafy adentro de las cuentas).
 */
export function normalizeAccounts(data) {
    const payload = data || {};
    const accounts = (payload.client_ad_accounts || []).filter(Boolean)
        .map((raw) => linkedRowOf(raw, isProfitability(raw) ? KIND_PROFITABILITY : KIND_ACCOUNT));
    const stores = (payload.client_stores || []).filter(Boolean).map((raw) => linkedRowOf(raw, KIND_STORE));
    return {
        [VIEW_LINKED]: accounts.concat(stores),
        [VIEW_UNLINKED]: (payload.unlinked_ad_accounts || []).filter(Boolean).map(unlinkedRowOf),
        [VIEW_IGNORED]: (payload.ignored_ad_accounts || []).filter((entry) => entry && entry.ad_account)
            .map(ignoredRowOf),
    };
}

// ------------------------------------------------------------------ búsqueda y plataforma

/** Minúsculas y sin tildes: «óptica» encuentra «Óptica» y «Optica». */
export function normalizeText(value) {
    return asText(value).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Por nombre, ID o cliente. Un ID se puede pegar con sus guiones («412-118-2290») aunque el
 * backend lo guarde sin ellos: si lo buscado son sólo dígitos y separadores, se comparan los
 * dígitos.
 */
export function matchesSearch(row, query) {
    const q = normalizeText(query).trim();
    if (!q) return true;
    if ([row.name, row.accountId, row.clientName].some((field) => normalizeText(field).includes(q))) {
        return true;
    }
    if (!/^[\d\s.-]+$/.test(q)) return false;
    const digits = q.replace(/\D/g, '');
    return digits.length >= 3 && asText(row.accountId).replace(/\D/g, '').includes(digits);
}

export function matchesPlatform(row, platform) {
    return !platform || row.channel === platform;
}

export function filterRows(rows, {platform = null, query = '', unreachableOnly = false} = {}) {
    return (rows || []).filter((row) => matchesPlatform(row, platform) && matchesSearch(row, query)
        && (!unreachableOnly || isUnreachableInLine(row)));
}

/**
 * Cuántas filas tiene cada vista con la plataforma elegida. La búsqueda NO cuenta: el número del
 * segmentado es «cuántas hay acá», y si dependiera del texto cambiaría tecla a tecla.
 */
export function viewCounts(accounts, platform) {
    const counts = {};
    VIEWS.forEach((view) => {
        counts[view] = ((accounts && accounts[view]) || []).filter((row) => matchesPlatform(row, platform)).length;
    });
    return counts;
}

function countByChannel(rows) {
    const counts = {};
    (rows || []).forEach((row) => {
        counts[row.channel] = (counts[row.channel] || 0) + 1;
    });
    return counts;
}

export function compareNames(a, b) {
    return asText(a).localeCompare(asText(b), 'es', {sensitivity: 'base'});
}

/**
 * Los chips por plataforma de la vista abierta.
 *
 * Los chips son LOS MISMOS en las tres vistas (los canales que la agencia tiene en alguna) y en el
 * mismo orden (por cantidad total): uno que aparece y desaparece al cambiar de vista se lee como un
 * estado del botón. El número es el de la vista abierta, y en 0 el chip queda deshabilitado —salvo
 * que esté elegido, que siempre se tiene que poder apagar—. La búsqueda tampoco cuenta acá.
 */
export function platformChips(accounts, view, selected) {
    const all = VIEWS.reduce((rows, v) => rows.concat((accounts && accounts[v]) || []), []);
    const totals = countByChannel(all);
    if (selected && !totals[selected]) totals[selected] = 0;
    const inView = countByChannel((accounts && accounts[view]) || []);
    return Object.keys(totals)
        .map((channel) => ({
            channel,
            label: platformName(channel),
            count: inView[channel] || 0,
            total: totals[channel],
            selected: channel === selected,
        }))
        .map((chip) => ({...chip, disabled: chip.count === 0 && !chip.selected}))
        .sort((a, b) => (b.total - a.total) || compareNames(a.label, b.label));
}

// ------------------------------------------------------------------ grupos y páginas

// Dentro de un cliente, primero la pauta y después lo que la acompaña, como en Plataformas.
const FAMILY_RANK = {
    [CHANNEL_TYPE_PAID_MEDIA]: 0,
    [CHANNEL_TYPE_ANALYTICS]: 1,
    [CHANNEL_TYPE_SEO]: 2,
    [CHANNEL_TYPE_EMAIL_MARKETING]: 3,
    [CHANNEL_TYPE_ECOMMERCE]: 4,
    [CHANNEL_TYPE_PROFITABILITY]: 5,
};

function familyRank(row) {
    if (row.kind === KIND_PROFITABILITY) return FAMILY_RANK[CHANNEL_TYPE_PROFITABILITY];
    const info = CHANNEL_INFO_BY_CHANNEL[row.channel];
    const rank = info ? FAMILY_RANK[info.channelType] : undefined;
    return rank === undefined ? 9 : rank;
}

function compareInClient(a, b) {
    return (familyRank(a) - familyRank(b))
        || compareNames(platformName(a.channel), platformName(b.channel))
        || compareNames(a.name, b.name);
}

/** «Conectadas»: un grupo por cliente, en orden alfabético. Dos clientes homónimos son dos grupos. */
export function groupByClient(rows) {
    const byClient = new Map();
    (rows || []).forEach((row) => {
        const id = asText(row.clientId);
        if (!byClient.has(id)) {
            byClient.set(id, {key: `c-${id}`, clientId: row.clientId, clientName: row.clientName, rows: []});
        }
        byClient.get(id).rows.push(row);
    });
    const groups = Array.from(byClient.values());
    groups.forEach((group) => group.rows.sort(compareInClient));
    return groups.sort((a, b) => compareNames(a.clientName, b.clientName)
        || (Number(a.clientId) - Number(b.clientId)));
}

export const ACTIVITY_ACTIVE = 'ACTIVE';
export const ACTIVITY_DORMANT = 'DORMANT';
export const LIVENESS_DEAD = 'DEAD';

export const GROUP_ACTIVE = 'active';
export const GROUP_DORMANT = 'dormant';
export const GROUP_DEAD = 'dead';
export const GROUP_UNKNOWN = 'unknown';
export const ACTIVITY_GROUPS = [GROUP_ACTIVE, GROUP_DORMANT, GROUP_DEAD, GROUP_UNKNOWN];

/**
 * `{'<id de la cuenta en Dashbo>': candidato}` de `GET agency/global-lines/candidates/`.
 *
 * Se cruza por el `id` (el `AdAccount` de Dashbo), que es también el `id` de cada cuenta sin
 * cliente de `agency/ad-accounts/`. `null` si el pedido falló: la vista sale como lista simple, sin
 * afirmar nada sobre la actividad de nadie.
 */
export function candidatesByAdAccountPk(response) {
    if (!response || response.status !== 'OK' || !response.data || !Array.isArray(response.data.accounts)) {
        return null;
    }
    const byPk = {};
    response.data.accounts.forEach((candidate) => {
        if (candidate && candidate.id !== undefined && candidate.id !== null) {
            byPk[String(candidate.id)] = candidate;
        }
    });
    return byPk;
}

/**
 * El grupo de actividad de una cuenta sin cliente. Lo clasifica el backend
 * (`GlobalLineCandidatesService`, el mismo motor del túnel de alta y del modal «Vincular una
 * cuenta»): acá sólo se lee. El endpoint sólo trae paid media, así que GA4, Search Console y las
 * demás caen en «Sin datos de actividad», igual que una cuenta sin catálogo sincronizado.
 * `DEAD` (la plataforma la dio de baja) manda sobre la actividad.
 */
export function activityGroupOf(row, candidatesByPk) {
    const candidate = candidatesByPk ? candidatesByPk[asText(row.adAccountPk)] : null;
    if (!candidate) return GROUP_UNKNOWN;
    if (candidate.liveness === LIVENESS_DEAD) return GROUP_DEAD;
    if (candidate.activity === ACTIVITY_ACTIVE) return GROUP_ACTIVE;
    if (candidate.activity === ACTIVITY_DORMANT) return GROUP_DORMANT;
    return GROUP_UNKNOWN;
}

/**
 * «Sin cliente» agrupada por actividad (D9: por actividad, NO por gasto — el endpoint no trae
 * montos). Sin candidatos (`null`), un solo grupo sin título: la lista simple.
 */
export function groupUnlinked(rows, candidatesByPk) {
    const sorted = (rows || []).slice().sort((a, b) => compareNames(a.name, b.name));
    if (!candidatesByPk) return [{key: 'all', group: null, rows: sorted}];
    const buckets = {};
    ACTIVITY_GROUPS.forEach((group) => { buckets[group] = []; });
    sorted.forEach((row) => buckets[activityGroupOf(row, candidatesByPk)].push(row));
    return ACTIVITY_GROUPS.filter((group) => buckets[group].length)
        .map((group) => ({key: group, group, rows: buckets[group]}));
}

/** Cuántas cuentas sin cliente tuvieron actividad reciente; `null` si no se sabe. */
export function activeUnlinkedCount(rows, candidatesByPk) {
    if (!candidatesByPk) return null;
    return (rows || []).filter((row) => activityGroupOf(row, candidatesByPk) === GROUP_ACTIVE).length;
}

/** Ignoradas: una lista corrida, por nombre. */
export function sortIgnored(rows) {
    return (rows || []).slice().sort((a, b) => compareNames(a.name, b.name));
}

/**
 * Las primeras `limit` filas, sin perder los grupos: el corte puede caer en el medio de un cliente
 * y su encabezado igual dice cuántas cuentas tiene en total (`total`), no cuántas se ven.
 */
export function pageGroups(groups, limit) {
    let left = Math.max(0, limit);
    const page = [];
    (groups || []).forEach((group) => {
        if (left <= 0) return;
        const rows = group.rows.slice(0, left);
        page.push({...group, rows, total: group.rows.length});
        left -= rows.length;
    });
    return page;
}

export function rowsIn(groups) {
    return (groups || []).reduce((sum, group) => sum + group.rows.length, 0);
}

/** Cuántas trae «Mostrar N más»: hasta una página, nunca más de las que faltan. */
export function nextPageCount(shown, total, size = PAGE_SIZE) {
    return Math.max(0, Math.min(size, total - shown));
}

// ------------------------------------------------------------------ columnas de una fila

/**
 * El límite diario de una cuenta conectada: `na` si el canal no lo maneja (GA4, tiendas, Search
 * Console, Escalafy), `none` si lo maneja y no hay uno, `set` con su moneda, monto y tipo.
 */
export function dailyLimitOf(row) {
    const raw = row.raw || {};
    const info = CHANNEL_INFO_BY_CHANNEL[row.channel];
    if (row.kind !== KIND_ACCOUNT || !info || !info.dailyLimitEnabled) return {state: 'na'};
    const present = (value) => value !== null && value !== undefined && value !== '';
    if (!present(raw.daily_limit_type) || !present(raw.daily_limit_currency) || !present(raw.daily_limit_value)) {
        return {state: 'none'};
    }
    return {
        state: 'set',
        type: raw.daily_limit_type,
        currency: raw.daily_limit_currency,
        value: raw.daily_limit_value,
    };
}

/** «ARS 20.000»: plata exacta que alguien escribió a mano, en el idioma de la pantalla. */
export function formatLimitAmount(currency, value, language) {
    const number = Number(value);
    const amount = Number.isFinite(number) ? number.toLocaleString(language || undefined) : asText(value);
    return currency ? `${currency} ${amount}` : amount;
}

export function hasAccess(row) {
    return !row.raw || row.raw.has_access !== false;
}

/**
 * Una cuenta sin acceso que está en una línea de seguimiento ACTIVA: la que deja de actualizar algo que
 * la agencia mira. Las que no están en ninguna línea no cuentan (hay más de mil, muchas de líneas
 * abandonadas: el aviso no puede volverse ruido). Un backend viejo no manda `in_active_line`: ninguna
 * cuenta cuenta y el aviso no sale.
 *
 * Una cuenta CERRADA por la plataforma (`ACCOUNT_DISABLED`) tampoco: no es acceso perdido y reconectar
 * no la arregla (P-5 de la tanda, el mismo criterio que UD-19 en el backend). En la lista sigue con su
 * «Sin acceso».
 */
export function isUnreachableInLine(row) {
    return !hasAccess(row) && !!row.raw && row.raw.in_active_line === true
        && resolveErrorKind(row.raw.error_kind) !== ERROR_KIND_ACCOUNT_DISABLED;
}

/**
 * El aviso de arriba de Cuentas (M4), o `null` si no hay nada que avisar. Cuenta CUENTAS, no filas: la
 * misma cuenta conectada a dos clientes es una sola. `userName` y `channel` sólo cuando todas las ve el
 * mismo usuario de la misma plataforma (entonces el aviso lo nombra).
 */
export function unreachableNotice(linkedRows) {
    const byAccount = new Map();
    (linkedRows || []).filter(isUnreachableInLine).forEach((row) => {
        const key = row.raw.ad_account_id != null ? `pk-${row.raw.ad_account_id}` : `${row.channel}-${row.accountId}`;
        if (!byAccount.has(key)) byAccount.set(key, row);
    });
    const rows = Array.from(byAccount.values());
    if (!rows.length) return null;
    const names = new Set(rows.map((row) => row.raw.platform_user_name || null));
    const channels = new Set(rows.map((row) => row.channel));
    const shared = names.size === 1 && channels.size === 1 && !names.has(null);
    return {
        count: rows.length,
        userName: shared ? rows[0].raw.platform_user_name : null,
        channel: shared ? rows[0].channel : null,
    };
}

/**
 * Qué gesto recupera una cuenta caída, o `null` si ninguno de esta pantalla la recupera.
 *
 * - `sheet`: una planilla se arregla en su propia conexión (`EditSheetAdAccountModal`), como en la
 *   fila clásica (`ClientAdAccountRow`, «Arreglar conexión»).
 * - `store`: una tienda vuelve con `ReconnectAdAccountModal`, como en `ClientStoreRow`.
 * - `oauth`: paid media y GA4 se reconectan con el OAuth de la pantalla de conexiones. Es el gate de
 *   `AccountRows.supportsReconnect` (Panel 2.1) — copiado acá para que el modelo no arrastre React;
 *   `accountsModel.test.js` recorre los canales y falla si los dos dejan de contestar lo mismo.
 */
export function reconnectKindOf(row) {
    if (hasAccess(row)) return null;
    if (row.channel === SHEET_CHANNEL) return 'sheet';
    if (row.kind === KIND_STORE) return 'store';
    if (row.kind === KIND_PROFITABILITY) return null;
    if (supportsOAuthReconnect(row.channel) && !isFixedInTheSpreadsheet(row.raw && row.raw.error_kind)) {
        return 'oauth';
    }
    return null;
}

export function supportsOAuthReconnect(channel) {
    const normalized = channel === 'META' ? FACEBOOK_CHANNEL : channel;
    const info = CHANNEL_INFO_BY_CHANNEL[normalized];
    return !!info && (info.channelType === CHANNEL_TYPE_PAID_MEDIA || normalized === ANALYTICS_CHANNEL);
}

/**
 * El gesto de mantenimiento de una cuenta AL DÍA, o `null`. En el clásico una planilla abre su
 * conexión con el lápiz siempre (`ClientAdAccountRow`: URL, mapeo, filtros) y una tienda se vuelve a
 * autorizar siempre (`ClientStoreRow`), no sólo caídas: el drawer conserva los dos (review C-04).
 */
export function maintenanceKindOf(row) {
    if (!hasAccess(row)) return null;
    if (row.channel === SHEET_CHANNEL) return 'sheet';
    if (row.kind === KIND_STORE) return 'store';
    return null;
}

/**
 * Los clientes a los que ya está conectada la MISMA cuenta (canal + id de la plataforma). Una cuenta
 * puede estar en varios clientes, y «Cambiar de cliente» no puede ofrecer uno de ésos: el alta
 * rebota («account already linked to client») y el paso que falta no se pediría nunca (review C-02).
 */
export function linkedClientIdsOf(linkedRows, row) {
    return (linkedRows || [])
        .filter((other) => other.channel === row.channel && sameAccountId(other.accountId, row.accountId))
        .map((other) => other.clientId);
}

// El código tipado con que el alta rechaza por el tope de la suscripción. Vive con el resto del cupo
// (`utils/adAccountQuota`, UD-15); se reexporta para los que ya lo importaban de acá.
export {PLAN_LIMIT_CODE} from '../../../utils/adAccountQuota';

/** El código de una respuesta que no salió bien: el tipado del backend, o el del propio `APIClient`. */
export function errorCodeOf(response) {
    if (!response) return 'ERROR';
    return response.error_code || response.status || 'ERROR';
}

/**
 * El estado de una integración de la fila clásica (Looker Studio, Informes, Dashbo MCP): la misma
 * regla que `ClientAdAccountRow._getIntegrationStatus`. Es información, no una acción.
 */
export function integrationStatusOf(raw) {
    const data = raw && raw.looker_cc_status_data;
    if (!raw || !raw.has_connection || !data) return 'inactive';
    if (data.status === 'PROCESSING') return 'processing';
    if (data.status === 'COMPLETE' || (data.completed_percentage && data.completed_percentage >= 100)) return 'active';
    return 'inactive';
}

// ------------------------------------------------------------------ el buscador de clientes

/** Palabras de un nombre: sin tildes, sin signos («Casa Verde · TikTok» → casa, verde, tiktok). */
export function tokensOf(value) {
    return normalizeText(value).replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean);
}

function containsSequence(haystack, needle) {
    if (!needle.length || needle.length > haystack.length) return false;
    for (let start = 0; start + needle.length <= haystack.length; start += 1) {
        if (needle.every((token, index) => haystack[start + index] === token)) return true;
    }
    return false;
}

function sharedPrefix(a, b) {
    let length = 0;
    while (length < a.length && length < b.length && a[length] === b[length]) length += 1;
    return length;
}

/**
 * El cliente que se sugiere para una cuenta, por el nombre. Sólo entre los clientes que se le pasan,
 * que son los de la agencia (`agency/clients/active`): nunca se sugiere nada de afuera.
 *
 * Coincide si el nombre del cliente aparece ENTERO en el de la cuenta («Bodega Andina - Search» →
 * «Bodega Andina»; un cliente de una sola palabra, si tiene 3 letras o más), o si los dos arrancan
 * con las mismas dos palabras o más («Casa Verde · TikTok» → «Casa Verde Deco»). Gana la
 * coincidencia más larga; si dos clientes empatan, no se sugiere ninguno: una sugerencia tiene que
 * poder equivocarse sólo por el nombre de la cuenta, no por una elección nuestra al azar.
 */
export function suggestClient(accountName, clients) {
    const accountTokens = tokensOf(accountName);
    if (!accountTokens.length) return null;
    let best = null;
    let bestScore = 0;
    let tied = false;
    (clients || []).forEach((client) => {
        const clientTokens = tokensOf(client && client.name);
        if (!clientTokens.length) return;
        let score = 0;
        const longEnough = clientTokens.length > 1 || clientTokens[0].length >= 3;
        if (longEnough && containsSequence(accountTokens, clientTokens)) {
            score = 100 + clientTokens.join(' ').length;
        } else {
            const prefix = sharedPrefix(accountTokens, clientTokens);
            if (prefix >= 2) score = prefix;
        }
        if (score > bestScore) {
            best = client;
            bestScore = score;
            tied = false;
        } else if (score > 0 && score === bestScore) {
            tied = true;
        }
    });
    return tied ? null : best;
}

/**
 * Lo que muestra el buscador de clientes: la sugerencia (si coincide con lo tipeado) y el resto en
 * orden alfabético, sin repetirla y sin los clientes excluidos (el actual, al cambiar de cliente).
 */
export function pickerOptions(clients, query, suggested, excludeIds = []) {
    const q = normalizeText(query).trim();
    const excluded = new Set((excludeIds || []).map(asText));
    const matches = (client) => !q || normalizeText(client.name).includes(q);
    const pool = (clients || []).filter((client) => client && client.name && !excluded.has(asText(client.id)));
    const shownSuggestion = suggested && !excluded.has(asText(suggested.id)) && matches(suggested) ? suggested : null;
    const others = pool
        .filter((client) => matches(client) && !(shownSuggestion && asText(client.id) === asText(shownSuggestion.id)))
        .sort((a, b) => compareNames(a.name, b.name));
    return {suggested: shownSuggestion, others};
}

/** Las iniciales de un nombre, para el avatar de un cliente («Bodega Andina» → «BA»). */
export function initialsOf(name) {
    const words = asText(name).trim().split(/\s+/).filter(Boolean);
    return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase();
}

// ------------------------------------------------------------------ acciones en lote

/**
 * Corre `worker` sobre cada ítem, UNO POR UNO (contrato §3: en serie, con progreso). Un ítem que
 * falla —o que tira— no frena a los demás, salvo que `stopWhen(resultado)` diga que no tiene sentido
 * seguir (el tope del plan: las que faltan rebotarían igual). Las que no se llegaron a correr van
 * como `{ok: false, skipped: true}`.
 *
 * `worker` devuelve un booleano o `{ok, code}`. Devuelve `[{item, ok, code, skipped}]` en orden.
 */
export async function runInSeries(items, worker, onProgress, {stopWhen} = {}) {
    const results = [];
    for (let index = 0; index < items.length; index += 1) {
        let result;
        try {
            const value = await worker(items[index]);
            result = value && typeof value === 'object'
                ? {item: items[index], ok: !!value.ok, code: value.code || null}
                : {item: items[index], ok: !!value, code: null};
        } catch (error) {
            result = {item: items[index], ok: false, code: null};
        }
        results.push(result);
        if (onProgress) onProgress(index + 1, items.length);
        if (stopWhen && stopWhen(result)) {
            items.slice(index + 1).forEach((item) => results.push({item, ok: false, code: null, skipped: true}));
            break;
        }
    }
    return results;
}

/**
 * El resumen de un lote: cuántas salieron y cuáles quedan (las que fallan, y las que no se llegaron a
 * correr, siguen seleccionadas). `stoppedBy` es el código que cortó el lote, si alguno lo cortó.
 */
export function bulkOutcome(results) {
    const list = results || [];
    const failed = list.filter((result) => !result.ok).map((result) => result.item);
    const stopper = list.find((result, index) => !result.ok && list[index + 1] && list[index + 1].skipped);
    return {total: list.length, ok: list.length - failed.length, failed, stoppedBy: stopper ? stopper.code : null};
}

/** La selección, sin las claves que ya no están entre las filas que se ven. */
export function pruneSelection(selectedKeys, rows) {
    const present = new Set((rows || []).map((row) => row.key));
    const next = new Set();
    (selectedKeys || []).forEach((key) => {
        if (present.has(key)) next.add(key);
    });
    return next;
}
